/**
 * MODAL: ASSIGN CONTACT
 * Modale pour associer un contact à une école
 */

import { useState, useMemo, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Modal } from '../ui/modal';
import { Badge } from '../ui';
import { useSupabase } from '@/hooks/useSupabase';
import type { Contact } from '@/types/contact';
import { CONTACT_STATUS_LABELS, CONTACT_STATUS_COLORS } from '@/types/contact';

interface AssignContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (contactId: string) => Promise<void>;
  excludeContactIds?: string[];
}

export function AssignContactModal({
  isOpen,
  onClose,
  onAssign,
  excludeContactIds = [],
}: AssignContactModalProps) {
  const supabase = useSupabase();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Charger les contacts à l'ouverture du modal
  useEffect(() => {
    if (!isOpen) return;

    async function loadContacts() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, email, phone, status, school_id')
          .is('school_id', null) // Uniquement les contacts sans école
          .order('last_name', { ascending: true });

        if (!error && data) {
          setContacts(data as Contact[]);
        }
      } finally {
        setLoading(false);
      }
    }

    loadContacts();
  }, [isOpen, supabase]);

  // Filtrer les contacts
  const filteredContacts = useMemo(() => {
    let filtered = contacts.filter((contact) => !excludeContactIds.includes(contact.id));

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (contact) =>
          contact.first_name.toLowerCase().includes(term) ||
          contact.last_name.toLowerCase().includes(term) ||
          contact.email.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [contacts, searchTerm, excludeContactIds]);

  async function handleAssign(contactId: string) {
    setIsAssigning(true);
    try {
      await onAssign(contactId);
      onClose();
      setSearchTerm('');
    } finally {
      setIsAssigning(false);
    }
  }

  function handleClose() {
    setSearchTerm('');
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Associer un contact" size="md">
      {/* Champ de recherche */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un contact..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-900"
            autoFocus
          />
        </div>
      </div>

      {/* Liste des contacts */}
      {loading ? (
        <div className="text-center py-8 text-sm text-zinc-500">Chargement...</div>
      ) : filteredContacts.length > 0 ? (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredContacts.map((contact) => (
            <button
              key={contact.id}
              onClick={() => handleAssign(contact.id)}
              disabled={isAssigning}
              className="w-full text-left p-3 rounded-lg border border-zinc-200 hover:bg-zinc-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900 truncate">
                    {contact.first_name} {contact.last_name}
                  </div>
                  <div className="text-sm text-zinc-500 mt-0.5 truncate">{contact.email}</div>
                </div>
                <Badge
                  className={`text-xs flex-shrink-0 ${CONTACT_STATUS_COLORS[contact.status].bg} ${CONTACT_STATUS_COLORS[contact.status].text} border ${CONTACT_STATUS_COLORS[contact.status].border}`}
                >
                  {CONTACT_STATUS_LABELS[contact.status]}
                </Badge>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-sm text-zinc-500">
          {searchTerm.trim()
            ? 'Aucun contact trouvé'
            : 'Aucun contact disponible (tous les contacts ont déjà une école)'}
        </div>
      )}
    </Modal>
  );
}
